/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnDestroy, OnInit, signal} from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {firstValueFrom} from 'rxjs';

import template from './advertiser-invite-editor-modal.component.html';
import {AdvertiserApiService} from './advertiser-api.service';
import {PoliticalCampaignApiService} from '../political-campaign/political-campaign-api.service';
import {PublicPoliticalCampaignApiService} from '../political-campaign/public-political-campaign-api.service';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {AppShellStateService} from '../base/app-shell-state.service';
import {AdvertisingSharedModule} from '../advertising/advertising-shared.module';

const POLITICAL_CAMPAIGN_GROUP_PAGE_SIZE = 2000;

@Component({
  standalone: true,
  imports: [AdvertisingSharedModule],
  selector: 'adn-advertiser-invite-editor-modal',
  template
})
export class AdvertiserInviteEditorModalComponent implements OnInit, OnDestroy {
  /** Set via BsModalService initialState */
  advertiserId: string;
  defaultSubject: string;

  readonly inviteSending = signal(false);
  readonly inviteSent = signal(false);
  inviteEmail = '';
  inviteName = '';
  inviteSubject = '';
  readonly inviteError = signal('');
  readonly politicalInviteLoading = signal(true);
  readonly politicalInviteLoadFailed = signal(false);
  readonly politicalInviteExpiring = signal(false);
  readonly currentPoliticalInvite = signal<any>(null);
  readonly availablePoliticalOrganisations = signal<any[]>([]);
  selectedPoliticalOrganisation: any = null;
  private removeNetworksChangeListener: (() => void) | null = null;

  constructor(
    public bsModalRef: BsModalRef,
    private readonly advertiserApi: AdvertiserApiService,
    private readonly politicalCampaignApi: PoliticalCampaignApiService,
    private readonly publicPoliticalCampaignApi: PublicPoliticalCampaignApiService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly shell: AppShellStateService
  ) {
  }

  ngOnInit(): void {
    this.inviteSubject = this.defaultSubject || '';
    this.loadCurrentPoliticalInvite();
  }

  private loadPoliticalOrganisationsWhenReady(): void {
    if (this.shell.networksLoaded) {
      this.loadPoliticalOrganisations();
      return;
    }
    this.removeNetworksChangeListener = this.shell.onNetworksChange(() => {
      if (this.shell.networksLoaded) {
        this.removeNetworksChangeListener?.();
        this.removeNetworksChangeListener = null;
        this.loadPoliticalOrganisations();
      }
    });
  }

  ngOnDestroy(): void {
    this.removeNetworksChangeListener?.();
  }

  sendInvite(): void {
    if (this.currentPoliticalInvite() || this.politicalInviteLoading() || this.politicalInviteLoadFailed()) {
      return;
    }
    this.inviteSending.set(true);
    this.inviteError.set('');
    const subject = this.inviteSubject === this.defaultSubject ? '' : this.inviteSubject;
    const request = this.selectedPoliticalOrganisation
      ? this.advertiserApi.emailPoliticalEntityInvite({
        id: this.advertiserId,
        idOfPoliticalEntity: this.selectedPoliticalOrganisation.publicId || this.selectedPoliticalOrganisation.id,
        subject
      })
      : this.advertiserApi.emailInvite({
        id: this.advertiserId,
        name: this.inviteName,
        email: this.inviteEmail,
        subject
      });
    firstValueFrom(request).then(() => {
      this.inviteSending.set(false);
      this.inviteSent.set(true);
      if (this.selectedPoliticalOrganisation) {
        this.currentPoliticalInvite.set({
          idOfPoliticalEntity: this.selectedPoliticalOrganisation.publicId || this.selectedPoliticalOrganisation.id,
          name: this.selectedPoliticalOrganisation.name
        });
      }
    }, (response: any) => {
      this.inviteSending.set(false);
      this.inviteError.set(response.data.text);
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  expirePoliticalInvite(): void {
    if (!this.currentPoliticalInvite() || this.politicalInviteExpiring()) {
      return;
    }
    this.politicalInviteExpiring.set(true);
    this.inviteError.set('');
    firstValueFrom(this.advertiserApi.expirePoliticalEntityInvite({
      id: this.advertiserId,
      expiry: new Date(0).toISOString()
    })).then(() => {
      this.politicalInviteExpiring.set(false);
      this.currentPoliticalInvite.set(null);
      this.selectedPoliticalOrganisation = null;
      this.inviteSent.set(false);
      this.loadPoliticalOrganisationsWhenReady();
    }, (response: any) => {
      this.politicalInviteExpiring.set(false);
      this.inviteError.set(response.data?.text || 'The linkage request could not be cancelled.');
    });
  }

  private loadCurrentPoliticalInvite(): void {
    this.politicalInviteLoading.set(true);
    this.politicalInviteLoadFailed.set(false);
    firstValueFrom(this.advertiserApi.currentPoliticalEntityInvite(this.advertiserId)).then((response: any) => {
      this.currentPoliticalInvite.set(response.invitation || null);
      this.politicalInviteLoading.set(false);
      if (!this.currentPoliticalInvite()) {
        this.loadPoliticalOrganisationsWhenReady();
      }
    }, (response: any) => {
      this.politicalInviteLoading.set(false);
      this.politicalInviteLoadFailed.set(true);
      this.inviteError.set(response.data?.text || 'The current linkage request could not be loaded.');
    });
  }

  private loadPoliticalOrganisations(): void {
    const politicalNetworkId = this.findPoliticalManageSystemNetwork();
    const params: any = {
      includeHidden: false,
      includeActive: true,
      includeInactive: false,
      pageSize: POLITICAL_CAMPAIGN_GROUP_PAGE_SIZE
    };
    const request = politicalNetworkId
      ? firstValueFrom(this.politicalCampaignApi.grouped({
        ...params,
        context: politicalNetworkId,
        visibilityType: 'STANDARD;TEST'
      }))
      : firstValueFrom(this.publicPoliticalCampaignApi.grouped(params));

    request.then((page: any) => {
      const organisationsById = new Map<string, any>();
      (page.results || []).forEach((group: any) => {
        [group.controller, group.sponsor].forEach((organisation: any) => {
          const id = organisation?.publicId || organisation?.id;
          if (id) {
            organisationsById.set(id, organisation);
          }
        });
      });
      const allowedVisibilityTypes = politicalNetworkId ? ['STANDARD', 'TEST'] : ['STANDARD'];
      this.availablePoliticalOrganisations.set(Array.from(organisationsById.values())
        .filter((organisation: any) => {
          const type = organisation.type === 'CONTROLLING' ? 'CONTROLLER' : organisation.type;
          const visibilityType = (organisation.visibilityType || 'STANDARD').toUpperCase();
          return (type === 'CONTROLLER' || type === 'SPONSOR')
            && allowedVisibilityTypes.includes(visibilityType)
            && (!organisation.validationWarnings || organisation.validationWarnings.length === 0);
        })
        .map((organisation: any) => ({
          ...organisation,
          inviteLabel: organisation.contactEmail
            ? `${organisation.name} — ${organisation.contactEmail}`
            : organisation.name
        }))
        .sort((left: any, right: any) => left.inviteLabel.localeCompare(right.inviteLabel)));
    }, () => {
      this.availablePoliticalOrganisations.set([]);
    });
  }

  private findPoliticalManageSystemNetwork(): string | null {
    const network = (this.shell.networks.POLITICAL || []).find((candidate: any) => {
      const networkId = candidate.tagId || candidate.id;
      return networkId && this.LocalUserPermissions.hasNetworkPermission('MANAGE_SYSTEM', networkId);
    });
    return network ? network.tagId || network.id : null;
  }
}
